import { Link } from "react-router-dom";
import { MapPin, Instagram, Facebook } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { Logo } from "@/components/Logo";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const TikTokIcon = ({ size = 18, ...props }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" {...props}>
    <path d="M16.6 5.82c-1-.93-1.5-2.16-1.5-3.62h-3.1v13.87c0 1.62-1.32 2.94-2.94 2.94a2.94 2.94 0 0 1 0-5.88c.3 0 .59.05.86.13V9.99a6.05 6.05 0 0 0-.86-.06 6.05 6.05 0 1 0 6.05 6.05V8.62c1.15.82 2.55 1.3 4.06 1.3V6.83c-.9 0-1.79-.36-2.57-1.01Z" />
  </svg>
);

export const Footer = () => {
  const { settings, waLink } = useSettings();
  return (
    <footer className="relative bg-ink text-cream overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-20 md:pt-28 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-2 mb-5">
              <Logo src={settings.logo_url} size={28} />
              <span className="font-brand font-bold text-xl">{settings.brand_name}</span>
            </div>
            <p className="text-cream/70 max-w-md leading-relaxed">
              {settings.tagline}. Madu murni tanpa campuran, dipanen langsung dari
              peternak lebah lokal di {settings.location_text}.
            </p>
            <a
              data-testid="footer-whatsapp"
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-wa px-6 py-3.5 font-semibold text-white hover:opacity-90 transition-opacity duration-300"
            >
              Hubungi via WhatsApp
            </a>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-sm uppercase tracking-widest text-honey-light mb-5">Navigasi</h4>
            <ul className="space-y-3 text-cream/70">
              {[
                ["Tentang", "tentang"],
                ["Manfaat", "manfaat"],
                ["Produk", "produk"],
                ["Galeri", "galeri"],
                ["Testimoni", "testimoni"],
                ["Pesan", "pesan"],
              ].map(([label, id]) => (
                <li key={id}>
                  <button
                    onClick={() => scrollTo(id)}
                    className="hover:text-honey transition-colors duration-300"
                  >
                    {label}
                  </button>
                </li>
              ))}
              <li>
                <Link to="/artikel" className="hover:text-honey transition-colors duration-300">
                  Artikel
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-sm uppercase tracking-widest text-honey-light mb-5">Kontak</h4>
            <div className="flex items-start gap-3 text-cream/70 mb-4">
              <MapPin size={18} className="mt-0.5 text-honey shrink-0" />
              <span>{settings.location_text}, Indonesia</span>
            </div>
            <div className="text-cream/70 mb-6">{settings.email}</div>
            <div className="flex gap-3">
              {settings.instagram_url && (
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 hover:bg-honey hover:text-ink hover:border-honey transition-colors duration-300"
                >
                  <Instagram size={18} />
                </a>
              )}
              {settings.facebook_url && (
                <a
                  href={settings.facebook_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Facebook"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 hover:bg-honey hover:text-ink hover:border-honey transition-colors duration-300"
                >
                  <Facebook size={18} />
                </a>
              )}
              {settings.tiktok_url && (
                <a
                  href={settings.tiktok_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="TikTok"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 hover:bg-honey hover:text-ink hover:border-honey transition-colors duration-300"
                >
                  <TikTokIcon size={18} />
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-10 md:mt-16 mb-6 md:mb-10 font-display text-[15vw] leading-none tracking-tighter text-cream/[0.06] select-none pointer-events-none whitespace-nowrap overflow-hidden">
          {settings.brand_name}
        </div>

        <div className="border-t border-cream/10 pt-8 flex flex-col md:flex-row justify-between gap-4 text-sm text-cream/50">
          <span>© {new Date().getFullYear()} {settings.brand_name}. Semua hak dilindungi.</span>
          <span className="flex items-center gap-4">
            Dibuat dengan ♥ di Jombang, Jawa Timur
            <Link to="/admin" className="text-cream/30 hover:text-honey transition-colors duration-300">
              Admin
            </Link>
          </span>
        </div>
      </div>
    </footer>
  );
};
